import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ImageIcon, Loader2Icon, MinusIcon, PlusIcon, ScanIcon, XIcon } from 'lucide-react';
import { useZoomPan } from './useZoomPan';
import { preloadImage, preloadImages } from '../../utils/preload';
export interface LightboxItem {
  src: string;
  alt: string;
  caption: string;
  /** Descriptive file name, shown while the image is not uploaded yet. */
  file?: string;
}
interface LightboxProps {
  items: LightboxItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label: string;
  counterNoun?: string;
}

/** Window event other UI (the sticky purchase bar) listens to. */
export const LIGHTBOX_EVENT = 'pp-lightbox-toggle';
const iconButton = 'flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/70';

/**
 * Full-screen viewer for original pages. Pinch / wheel / double-tap / buttons
 * to zoom, drag to pan, swipe or arrow keys to change page, Escape to close.
 * Focus is trapped while open and restored on close.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  label,
  counterNoun = 'Página'
}: LightboxProps) {
  const open = index !== null;
  const current = index !== null ? items[index] : null;
  const count = items.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const go = useCallback((direction: 1 | -1) => {
    if (index === null) return;
    const next = index + direction;
    if (next >= 0 && next < count) onNavigate(next);
  }, [index, count, onNavigate]);
  const zoom = useZoomPan(stageRef, imageRef, {
    onSwipe: go
  });
  const {
    reset
  } = zoom;

  // Keep the previous page on screen until the next one is decoded.
  const [shown, setShown] = useState<LightboxItem | null>(null);
  const loading = Boolean(current && shown !== current);
  useEffect(() => {
    if (!current || index === null) {
      setShown(null);
      return;
    }
    if (!current.src) {
      setShown(current);
      return;
    }
    let cancelled = false;
    preloadImage(current.src).then(() => {
      if (!cancelled) setShown(current);
    });
    const neighbours = [items[index - 1], items[index + 1]].filter(item => item && item.src).map(item => item.src);
    preloadImages(neighbours);
    return () => {
      cancelled = true;
    };
  }, [current, index, items]);
  useEffect(() => {
    reset();
  }, [shown, reset]);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
      detail: true
    }));
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus({
      preventScroll: true
    }));
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
        detail: false
      }));
      previousFocus?.focus?.({
        preventScroll: true
      });
      if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo({
        top: scrollY,
        behavior: 'auto'
      });
    };
  }, [open]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowRight':
        event.preventDefault();
        go(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        go(-1);
        break;
      case '+':
      case '=':
        zoom.zoomIn();
        break;
      case '-':
        zoom.zoomOut();
        break;
      case 'Tab':
        {
          const all = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
          const nodes = Array.from(all ?? []).filter(node => node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
          if (nodes.length === 0) return;
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          break;
        }
      default:
        break;
    }
  };
  if (typeof document === 'undefined') return null;
  const transform = `translate3d(${zoom.offset.x}px, ${zoom.offset.y}px, 0) scale(${zoom.scale})`;
  const transition = zoom.interacting ? 'opacity 150ms ease-out' : 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease-out';
  return createPortal(<AnimatePresence data-id="element-89" data-mp="1sikbja52a3st">
      {open && current && index !== null && <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label}: ${current.caption}`} onKeyDown={onKeyDown} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex flex-col bg-graphite-deep/[0.97] text-white" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-90" data-mp="1ogqcf6ail7ur">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-2 sm:px-6" data-id="element-91" data-mp="2l7isw51d11x2">
            <div className="min-w-0" aria-live="polite" data-id="element-92" data-mp="2g5tk6w1m10c5">
              <p className="text-[0.8rem] font-bold text-coral" data-id="element-93" data-mp="1dvjg1ie5qbw6">
                {counterNoun} {index + 1} de {count}
              </p>
              <p className="truncate text-[1rem] font-semibold leading-tight" data-id="element-94" data-mp="1irgxxt52jqb2">{current.caption}</p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className={iconButton} data-id="element-95" data-mp="1rlvahny7smqh">
              <XIcon className="h-5 w-5" aria-hidden data-id="element-96" data-mp="1wl87h1isva6s" />
            </button>
          </div>

          <div ref={stageRef} {...zoom.handlers} className="relative min-h-0 flex-1 touch-none select-none overflow-hidden" style={{
        cursor: zoom.scale > 1 ? zoom.interacting ? 'grabbing' : 'grab' : 'zoom-in'
      }} data-id="element-97" data-mp="21k2n5ulh0imo">
            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6" data-id="element-98" data-mp="2ain1yknwu85m">
              {shown && shown.src && <img ref={imageRef} key={shown.src} src={shown.src} alt={shown.alt} draggable={false} className={`max-h-full max-w-full rounded-[2px] bg-paper object-contain ${loading ? 'opacity-60' : 'opacity-100'}`} style={{
            transform,
            transition
          }} data-id="element-99" data-mp="g6gdik609uvd" />}
              {shown && !shown.src && <div role="img" aria-label={shown.alt} className="relative flex aspect-[1240/1754] h-full max-h-full max-w-full flex-col items-center justify-center gap-2 rounded-[2px] bg-paper p-6 text-center" style={{
            transform,
            transition
          }} data-id="element-100" data-mp="3r2rer6chbkmx">
                  <span aria-hidden className="absolute inset-[5%] rounded-[4px] border border-dashed border-coral/40" data-id="element-101" data-mp="dmaaqft4oage" />
                  <ImageIcon className="h-6 w-6 text-coral/70" aria-hidden data-id="element-102" data-mp="33c5c5ul8uskb" />
                  <span className="max-w-[80%] break-all font-mono text-[12px] text-graphite-soft" data-id="element-103" data-mp="2yciwlehy108c">{shown.file}</span>
                </div>}
            </div>
            {loading && <div className="pointer-events-none absolute inset-0 flex items-center justify-center" role="status" data-id="element-104" data-mp="16azr09pci8kn">
                <span className="flex items-center gap-2 rounded-full bg-graphite/90 px-3.5 py-2 text-[0.85rem] font-semibold text-ivory ring-1 ring-white/15" data-id="element-105" data-mp="sbq8h3do32di">
                  <Loader2Icon className="h-4 w-4 animate-spin text-coral" aria-hidden data-id="element-106" data-mp="xce1fulghtad" />
                  Cargando página…
                </span>
              </div>}

            <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Página anterior" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute left-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-107" data-mp="1pyfle89wqze5">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-108" data-mp="28p6o4qze3rjk" />
            </button>
            <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Página siguiente" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute right-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-109" data-mp="28r9qmahqbd56">
              <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-110" data-mp="2295bincppkx" />
            </button>
          </div>

          <div className="shrink-0 border-t border-white/10 px-3 pb-2 pt-2 sm:px-6" data-id="element-111" data-mp="3ahsqdudvc1ow">
            <div className="flex items-center justify-between gap-2" data-id="element-112" data-mp="35iofmzbapvwg">
              <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Página anterior" className={`${iconButton} md:invisible`} data-id="element-113" data-mp="3jhfuinxzflln">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-114" data-mp="ypvtkta9u1na" />
              </button>

              <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-115" data-mp="trkjchg2dp4j">
                <button type="button" onClick={zoom.zoomOut} disabled={!zoom.canZoomOut} aria-label="Alejar" className={iconButton} data-id="element-116" data-mp="dw4erpaqeyss">
                  <MinusIcon className="h-5 w-5" aria-hidden data-id="element-117" data-mp="8xt4jdgiyma1" />
                </button>
                <span className="w-12 text-center text-[14px] font-semibold tabular-nums text-white/80" data-id="element-118" data-mp="44qal47u5atb">
                  {Math.round(zoom.scale * 100)}%
                </span>
                <button type="button" onClick={zoom.zoomIn} disabled={!zoom.canZoomIn} aria-label="Acercar" className={iconButton} data-id="element-119" data-mp="3rb0qkwv8r24m">
                  <PlusIcon className="h-5 w-5" aria-hidden data-id="element-120" data-mp="3mamumd6wzimy" />
                </button>
                <button type="button" onClick={zoom.reset} disabled={!zoom.canZoomOut} aria-label="Ajustar a la pantalla" className={iconButton} data-id="element-121" data-mp="3hdct3z693gir">
                  <ScanIcon className="h-5 w-5" aria-hidden data-id="element-122" data-mp="1filxiz4zenv3" />
                </button>
              </div>

              <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Página siguiente" className={`${iconButton} md:invisible`} data-id="element-123" data-mp="2tgzqlh9vghpy">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-124" data-mp="337b6812x4fco" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[0.8rem] text-white/60" data-id="element-125" data-mp="377lbtcmst3r0">
              Pellizcá, usá la rueda o tocá dos veces para acercar · arrastrá para moverte
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}